import type { UseMutationOptions } from '@tanstack/react-query';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import type { FunctionalUseQueryOptions } from 'app/types/network';
import { http } from '../lib/axios';
import { useAtom, useAtomValue } from 'jotai';
import { bffUserProfileLoadedAtom, isLoggedInAtom, preferencesLoadedAtom } from 'app/atoms';
import { getBffQueryData } from 'app/lib/query';
import type { PreferenceName, Preference, PreferenceNameToValue } from 'app/types/preferences';
import { preferencesKeys } from './keys';
import { isFeatureEnabled, FeatureFlag, isWhiteLabel } from 'app/utils/helpers';
import { useQueryKeys } from './invalidation';
import { WhiteLabel } from 'app/types/white-labels';

export const DEFAULT_SHARE_RADIUS_IN_METERS = isWhiteLabel(WhiteLabel.HauteGaspesie)
  ? 125000
  : isWhiteLabel(WhiteLabel.Mutuo)
    ? 150000
    : isFeatureEnabled(FeatureFlag.justOneClub)
      ? 2000000
      : 5000;
export const MAX_RADIUS_IN_METERS = isWhiteLabel(WhiteLabel.HauteGaspesie)
  ? 125000
  : isWhiteLabel(WhiteLabel.Mutuo)
    ? 150000
    : isFeatureEnabled(FeatureFlag.justOneClub)
      ? 2000000
      : 35000;
export const DEFAULT_SEARCH_RADIUS_IN_METERS = MAX_RADIUS_IN_METERS;

export function usePreferences(opts: FunctionalUseQueryOptions<Record<string, string>[]> = {}) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const [preferencesLoaded, setPreferencesLoaded] = useAtom(preferencesLoadedAtom);
  const client = useQueryClient();

  const bffUserProfileLoaded = useAtomValue(bffUserProfileLoadedAtom);
  const queryKey = preferencesKeys.details();

  return useQuery<Record<string, string>[]>({
    queryKey: preferencesKeys.details(),
    queryFn: async () => {
      const resData =
        (bffUserProfileLoaded ? getBffQueryData<Record<string, string>[]>(client, queryKey) : undefined) ??
        (await http.get<Record<string, string>[]>('/preferences/')).data;

      // onSuccess
      resData.forEach((preference) =>
        client.setQueryData(preferencesKeys.detail(preference.name as PreferenceName), preference),
      );

      setPreferencesLoaded(true);

      return resData;
    },
    ...opts,
    enabled: isLoggedIn && !preferencesLoaded && bffUserProfileLoaded,
  });
}

export function useOnePreference<T extends PreferenceName>(
  name: T,
  opts: FunctionalUseQueryOptions<Preference<T>> = {},
) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const [preferencesLoaded] = useAtom(preferencesLoadedAtom);

  const client = useQueryClient();

  return useQuery<Preference<T>>({
    queryKey: preferencesKeys.detail(name),
    queryFn: () =>
      client.getQueryData(preferencesKeys.detail(name)) ??
      http.get<Preference<T>>(`/preferences/${name}`).then((res) => res.data),
    ...opts,
    enabled: isLoggedIn && preferencesLoaded,
  });
}

export function useUpsertOnePreference<T extends PreferenceName>(
  name: T,
  opts: UseMutationOptions<Preference<T> | null, Error, PreferenceNameToValue[T] | null> = {},
) {
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const client = useQueryClient();
  const qk = useQueryKeys();
  return useMutation<Preference<T> | null, Error, PreferenceNameToValue[T] | null>({
    mutationFn: (payload) => {
      if (!isLoggedIn) return Promise.resolve(null);
      return http.patch<Preference<T>>(`/preferences/${name}`, { name, value: payload }).then((res) => {
        void qk.preference.invalidate(name);
        return res.data;
      });
    },
    ...opts,
    onSuccess(result, variables, context) {
      if (result) {
        client.setQueryData<Preference<T>>(preferencesKeys.detail(name), result);
      } else {
        client.removeQueries({ queryKey: preferencesKeys.detail(name) });
      }
      return opts.onSuccess?.(result, variables, context);
    },
  });
}
